import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { SubMenu } from "./SubMenu";
import { Logo } from "./logo";
import styles from "./styles.module.css";
import { MenuItem, getMenuItems } from "./menuItems";
import { Paths } from "../../../paths";

interface SidebarProps {
  collapsed: boolean;
  onToggle?: () => void;
}

export const MainSidebar: React.FC<SidebarProps> = ({ collapsed, onToggle }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const storedData = JSON.parse(localStorage.getItem("availableRoutes") || "[]");

  console.log('Available Routes:', storedData);

  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [popoverStates, setPopoverStates] = useState<Record<string, boolean>>({});
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const menuItems = getMenuItems(t);

  const filterMenuItems = (items: MenuItem[], availableRoutes: any[]): MenuItem[] => {
    if (!Array.isArray(availableRoutes)) return [];

    const hasFullAccess = availableRoutes.some(route => route?.alias === '/interactive-map-ws');
    if (hasFullAccess) return items;

    const filteredItems = items.map(item => {
      if (item.subMenuKey === "sub-water") {
        const hasWaterAccess = availableRoutes.some(route => 
          (route?.alias === "/reservoir-history" || route?.alias === "/reservoir-register") && route.methodGet
        );
        
        if (hasWaterAccess) {
          return {
            ...item,
            paths: [Paths.mapAndStatistics],
            children: [{
              title: t('Water Resources Service'),
              paths: [Paths.mapAndStatistics],
              icon: "/sidebar/water.svg",
              subMenuKey: "mapAndStatistics",
              key: Paths.mapAndStatistics
            }]
          };
        }
        return null;
      }

      if (item.subMenuKey === "chemicalization") {
        const hasChemAccess = availableRoutes.some(route => 
          route?.alias?.startsWith('/departments/chemicalization') && route.methodGet
        );
        
        if (hasChemAccess) {
          return item;
        }
        return null;
      }

      if (item.subMenuKey === "mechanization") {
        const hasMechAccess = availableRoutes.some(route =>
          route?.alias?.startsWith('/departments/mechanization') && route.methodGet
        );

        if (hasMechAccess) {
          return item;
        }
        return null;
      }

      if (item.subMenuKey === "Gross") {
        const hasGrossAccess = availableRoutes.some(route =>
          route?.alias?.startsWith('/departments/organic') && route.methodGet
        );

        if (hasGrossAccess) {
          return item;
        }
        return null;
      }

      if (Array.isArray(item.children) && item.children.length > 0) {
        const filteredChildren = filterMenuItems(item.children, availableRoutes);
        
        if (filteredChildren.length > 0) {
          return { ...item, children: filteredChildren };
        }
        return null;
      }

      const routeData = availableRoutes.find(route => route?.alias === item.key);
      
      return routeData && routeData.methodGet && !routeData.error403 ? item : null;
    });

    return filteredItems.filter(Boolean) as MenuItem[];
  };

  const filteredMenuItems = filterMenuItems(menuItems, storedData);

  const handleOpenChange = (key: string) => {
    setOpenKeys(prevKeys => 
      prevKeys.includes(key) ? [] : [key]
    ); 
  };

  const handleLinkClick = (key: string) => {
    if (!openKeys.includes(key)) {
      setOpenKeys([...openKeys, key]);
    }
  };

  const togglePopover = (key: string) => {
    setPopoverStates(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  useEffect(() => {
    const currentKey = `/${location.pathname.split("/")[1]}`;
    if (!openKeys.includes(currentKey)) {
      setOpenKeys(prevKeys => [...prevKeys, currentKey]);
    }
  }, [location.pathname]);

  const renderMenuItem = (item: MenuItem) => {
    if (item.children && item.children.length > 0) {
      return (
        <SubMenu
          key={item.subMenuKey}
          title={item.title}
          paths={item.children.map(child => child.key)}
          labels={item.children.map(child => child.title)}
          collapsed={collapsed}
          isPopoverVisible={popoverStates[item.subMenuKey] || false}
          openKeys={openKeys}
          currentPath={location.pathname}
          subMenuKey={item.subMenuKey}
          onTogglePopover={() => togglePopover(item.subMenuKey)}
          onOpenChange={handleOpenChange}
          onLinkClick={handleLinkClick}
          setPopoverVisible={(visible) =>
            setPopoverStates(prev => ({ ...prev, [item.subMenuKey]: visible }))
          }
          icon={item.icon}
        />
      );
    }

    return (
      <SubMenu
        key={item.subMenuKey}
        title={item.title}
        paths={[item.key]}
        labels={[item.title]}
        collapsed={collapsed}
        isPopoverVisible={open[item.subMenuKey] || false}
        openKeys={openKeys}
        currentPath={location.pathname}
        subMenuKey={item.subMenuKey}
        onTogglePopover={() => togglePopover(item.subMenuKey)}
        onOpenChange={handleOpenChange}
        onLinkClick={handleLinkClick}
        setPopoverVisible={(visible) =>
          setPopoverStates(prev => ({ ...prev, [item.subMenuKey]: visible }))
        }
        icon={item.icon}
      />
    );
  };

  return (
    <div className={`${styles.sidebar} ${collapsed ? styles.collapsed : ""}`}>
      <Logo isCollapsed={collapsed} />
      <ul className={styles.menu}>
        {filteredMenuItems.map(renderMenuItem)}
      </ul>
      <div className={styles.login}></div>
    </div>
  );
};
